import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, HelpCircle, Target, Zap, Award, TrendingUp, Flame, ThermometerSun, Snowflake } from 'lucide-react'
import { cn } from '@/utils/cn'

// ─── Props ──────────────────────────────────────────────────────────────────

interface BdrHelpModalProps {
  isOpen: boolean
  onClose: () => void
}

// ─── Constants ──────────────────────────────────────────────────────────────

const cadenciaPhases = [
  {
    phase: 'F1',
    title: 'Abertura de Fluxo',
    color: '#14b8a6',
    attempts: 'T1 a T5',
    description: 'Primeiro contato e tentativa de conexão. Prioridade:Telefone e WhatsApp.',
  },
  {
    phase: 'F2',
    title: 'Conexão Ativa',
    color: '#10b981',
    attempts: 'T6 a T10',
    description: 'Follow-ups consistentes com alternância de canais. Marco: T10 = avaliação de viabilidade.',
  },
  {
    phase: 'F3',
    title: 'Follow-up / Maturação',
    color: '#f59e0b',
    attempts: 'T11 a T12',
    description: 'Última tentativa ativa antes da transição. Se T12 sem avanço → Nutrição.',
  },
  {
    phase: 'F4',
    title: 'Fechamento / Handoff',
    color: '#ef4444',
    attempts: 'T13 a T15',
    description: 'Tentativas finais com foco em agendamento. Se T15 sem sucesso → Descarte ou Nutrição.',
  },
]

const escopoCards = [
  {
    num: 1,
    title: 'Prospecção de Elite',
    description: 'Identificação e qualificação de leads com alto potencial de conversão usando inteligência de mercado.',
    icon: Target,
  },
  {
    num: 2,
    title: 'Qualificação de Inteligência',
    description: 'Scoring baseado em faturamento, decisor, plataforma e segmento para priorizar esforços.',
    icon: Zap,
  },
  {
    num: 3,
    title: 'Handoff de Alta Performance',
    description: 'Transferência de leads qualificados com contexto completo para o Closer agendar reunião.',
    icon: Award,
  },
]

const scoringTable = [
  { criteria: 'Faturamento acima de 1M', points: '+40 pts', color: 'text-red-400', icon: TrendingUp },
  { criteria: 'Decisor Direto (CEO/Diretor)', points: '+20 pts', color: 'text-amber-400', icon: Flame },
  { criteria: 'Plataforma Enterprise', points: '+15 pts', color: 'text-teal-400', icon: ThermometerSun },
  { criteria: 'Segmento Estratégico', points: '+10 pts', color: 'text-blue-400', icon: Snowflake },
]

const temperatureBadges = [
  { label: 'Hot', range: '70+', color: 'bg-red-500/15 text-red-400 border-red-500/20' },
  { label: 'Warm', range: '40-69', color: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
  { label: 'Cold', range: '0-39', color: 'bg-teal-500/15 text-teal-400 border-teal-500/20' },
]

// ─── Component ──────────────────────────────────────────────────────────────

export function BdrHelpModal({ isOpen, onClose }: BdrHelpModalProps) {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/95 backdrop-blur-[100px]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative z-10 w-full max-w-4xl max-h-[90vh] glass-card rounded-3xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center">
                  <HelpCircle size={18} className="text-accent" />
                </div>
                <div>
                  <h2 className="text-text-primary font-bold text-base">Guia Estratégico BDR</h2>
                  <p className="text-text-muted text-xs">Referência rápida para prospecção e qualificação</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-white/[0.06] text-text-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              {/* ── Section 1: Cadência de 15 Tentativas ────────────────── */}
              <section>
                <h3 className="text-text-primary font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-accent rounded-full" />
                  Cadência de 15 Tentativas
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cadenciaPhases.map(phase => (
                    <div
                      key={phase.phase}
                      className="glass-card p-4 border-l-2"
                      style={{ borderLeftColor: phase.color }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{
                            backgroundColor: `${phase.color}15`,
                            color: phase.color,
                          }}
                        >
                          {phase.phase}
                        </span>
                        <span className="text-text-primary font-semibold text-sm">
                          {phase.title}
                        </span>
                        <span className="text-text-muted text-[10px] ml-auto font-medium">
                          {phase.attempts}
                        </span>
                      </div>
                      <p className="text-text-muted text-xs leading-relaxed">
                        {phase.description}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Section 2: Escopo de Atuação BDR ───────────────────── */}
              <section>
                <h3 className="text-text-primary font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-accent rounded-full" />
                  Escopo de Atuação BDR
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {escopoCards.map(card => (
                    <div key={card.num} className="glass-card p-5 text-center">
                      <div className="w-10 h-10 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-3">
                        <card.icon size={20} className="text-accent" />
                      </div>
                      <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-2">
                        <span className="text-accent text-xs font-bold">{card.num}</span>
                      </div>
                      <h4 className="text-text-primary font-bold text-sm mb-2">{card.title}</h4>
                      <p className="text-text-muted text-xs leading-relaxed">{card.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Section 3: Lead Scoring ───────────────────────────── */}
              <section>
                <h3 className="text-text-primary font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-accent rounded-full" />
                  Lead Scoring
                </h3>

                <div className="glass-card overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/[0.06]">
                        <th className="text-left px-4 py-3 text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                          Critério
                        </th>
                        <th className="text-right px-4 py-3 text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                          Pontuação
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {scoringTable.map(row => (
                        <tr key={row.criteria} className="border-b border-white/[0.04] last:border-0">
                          <td className="px-4 py-3 flex items-center gap-2">
                            <row.icon size={14} className={row.color} />
                            <span className="text-text-primary text-sm">{row.criteria}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={cn('font-bold text-sm', row.color)}>
                              {row.points}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Temperature Badges */}
                <div className="flex items-center gap-3 mt-4">
                  <span className="text-text-muted text-xs font-semibold uppercase tracking-wider">
                    Temperatura:
                  </span>
                  {temperatureBadges.map(badge => (
                    <span
                      key={badge.label}
                      className={cn(
                        'px-3 py-1 rounded-full text-xs font-bold border',
                        badge.color
                      )}
                    >
                      {badge.label} ({badge.range})
                    </span>
                  ))}
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}
